import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { MILAN_WORKSPACE_ENABLED } from '../config/productFlags';
import './Services.css';

const ALL_SERVICES = [
  {
    id: 'perf-hub',
    icon: '📈',
    title: 'Performance Hub (Instagram)',
    isLive: true,
    workspaceOnly: false,
    description:
      'Upload your own creator list and post URLs, invite creators to connect Instagram once, and monitor verified insights.',
    features: [
      'CSV import (handle, email, post URL)',
      'Magic-link invites (no Milan signup for creators)',
      'Campaign & creator-level dashboards',
      'Daily sync + manual refresh',
      'CRM shortlist for top performers',
      'Paid plan: CSV export & higher limits',
    ],
    color: '#22c55e',
    ctaLink: '/performance',
    ctaLabel: 'Open Performance Hub',
  },
  {
    id: 'search',
    icon: '🔍',
    title: 'Creator Discovery',
    workspaceOnly: true,
    description: 'Find Instagram creators with our advanced search and filtering system.',
    features: [
      'Search by niche, location, follower count',
      'Filter by engagement rate',
      'View detailed creator profiles',
      'Save creators to your CRM',
      'AI-powered recommendations'
    ],
    color: '#3b82f6'
  },
  {
    id: 'analytics',
    icon: '📊',
    title: 'Performance Analytics',
    workspaceOnly: true,
    description: 'Track creator and campaign performance with Instagram metrics.',
    features: [
      'Real-time performance tracking',
      'Engagement metrics (likes, comments, views)',
      'Reach and impressions data',
      '1-4 week automated monitoring',
      'Campaign performance reports'
    ],
    color: '#8b5cf6'
  },
  {
    id: 'outreach',
    icon: '📧',
    title: 'Campaign Invitations',
    workspaceOnly: true,
    description: 'Invite creators to your campaigns with professional email invitations.',
    features: [
      'Campaign invitation emails',
      'Detailed campaign briefs',
      'Automated reminder system',
      'Accept/decline tracking',
      'Backup creator activation'
    ],
    color: '#ec4899'
  },
  {
    id: 'campaign',
    icon: '🎯',
    title: 'Campaign Management',
    workspaceOnly: true,
    description: 'End-to-end campaign execution from briefing to completion.',
    features: [
      'Campaign brief creation',
      'Content submission & approval',
      'Deadline tracking',
      'Revision management',
      'Real-time status updates'
    ],
    color: '#f59e0b'
  },
  {
    id: 'create',
    icon: '✨',
    title: 'Campaign Builder',
    workspaceOnly: true,
    description: 'Launch campaigns with our intuitive step-by-step campaign builder.',
    features: [
      'Step-by-step campaign wizard',
      'Budget allocation tools',
      'Target audience definition',
      'Primary & backup creator selection',
      'Content script templates'
    ],
    color: '#10b981'
  },
  {
    id: 'recommend',
    icon: '🤖',
    title: 'AI Recommendations',
    workspaceOnly: true,
    description: 'Get AI-powered creator suggestions based on your campaign requirements.',
    features: [
      'Budget-optimized suggestions',
      'Niche and location matching',
      'Engagement-based ranking',
      'Follower range targeting',
      'Automatic shortlisting'
    ],
    color: '#6366f1'
  },
  {
    id: 'content',
    icon: '🎨',
    title: 'Content Management',
    workspaceOnly: true,
    description: 'Review and approve creator content before publication.',
    features: [
      'Content submission workflow',
      'Up to 3 revision rounds',
      'Approval/rejection system',
      'Content URL verification',
      'Published content tracking'
    ],
    color: '#14b8a6'
  },
  {
    id: 'payments',
    icon: '💳',
    title: 'Secure Escrow Payments',
    workspaceOnly: true,
    description: 'Safe and transparent payment processing with performance-based releases.',
    features: [
      'Escrow-protected throughout campaign',
      '1-4 week monitoring period (brand choice)',
      '60% fixed released weekly during monitoring',
      '40% performance released after monitoring',
      '3.5% platform fee on success'
    ],
    color: '#f97316'
  }
];

/** Extra PH capability cards when campaigns are hidden — keeps Services visually full. */
const PH_EXTRA_SERVICES = [
  {
    id: 'ph-economics',
    icon: '💰',
    title: 'Campaign Economics',
    workspaceOnly: false,
    description: 'See spend efficiency with CPM/CPE-style views tied to the posts you track.',
    features: [
      'Per-campaign and per-creator economics',
      'Budget context you enter yourself',
      'Compare relative efficiency across roster',
      'Export-friendly on paid tiers',
    ],
    color: '#0ea5e9',
    ctaLink: '/performance',
    ctaLabel: 'Open Performance Hub',
  },
  {
    id: 'ph-intel',
    icon: '🧠',
    title: 'Creator & Comment Intelligence',
    workspaceOnly: false,
    description: 'Go beyond vanity metrics — creative patterns and comment themes that inform the next brief.',
    features: [
      'Creator intelligence summaries',
      'Comment intelligence themes',
      'Creative DNA style signals',
      'Post Guard brief checks',
    ],
    color: '#8b5cf6',
    ctaLink: '/performance',
    ctaLabel: 'Explore in Hub',
  },
  {
    id: 'ph-crm',
    icon: '📌',
    title: 'Private CRM Shortlist',
    workspaceOnly: false,
    description: 'Save top performers inside Performance Hub so your next flight starts from winners.',
    features: [
      'Shortlist from overview',
      'Workspace-private tags',
      'Built for repeat briefings',
      'No public creator marketplace',
    ],
    color: '#f59e0b',
    ctaLink: '/performance',
    ctaLabel: 'Open Performance Hub',
  },
];

function Services() {
  const services = MILAN_WORKSPACE_ENABLED
    ? ALL_SERVICES
    : [...ALL_SERVICES.filter((s) => !s.workspaceOnly), ...PH_EXTRA_SERVICES];

  return (
    <div className="services-page">
      <Navbar />
      
      <section className="services-hero">
        <h1>Our Services</h1>
        <p>
          {MILAN_WORKSPACE_ENABLED
            ? 'Everything you need to run successful influencer marketing campaigns'
            : 'Instagram performance tracking for brands and agencies who already have a creator roster'}
        </p>
      </section>

      <section className="services-overview">
        <div className="overview-container">
          <div className="overview-content">
            <span className="overview-badge">
              {MILAN_WORKSPACE_ENABLED ? 'All-in-One Platform' : 'Performance Hub'}
            </span>
            <h2>
              {MILAN_WORKSPACE_ENABLED
                ? "From Discovery to Payment — We've Got You Covered"
                : 'From CSV upload to verified Instagram metrics'}
            </h2>
            <p>
              {MILAN_WORKSPACE_ENABLED
                ? 'Milan AI provides a complete suite of tools for brands to find Instagram creators, run campaigns, and manage payments — with only a small success fee.'
                : 'Upload creators and post URLs, invite Instagram connect via magic link, and monitor insights daily — without escrow or marketplace lock-in.'}
            </p>
            <div className="overview-stats">
              <div className="stat">
                <span className="stat-number">Instagram</span>
                <span className="stat-text">{MILAN_WORKSPACE_ENABLED ? 'Creator Network' : 'Insights live'}</span>
              </div>
              <div className="stat">
                <span className="stat-number">{services.length}</span>
                <span className="stat-text">Core capabilities</span>
              </div>
              <div className="stat">
                <span className="stat-number">{MILAN_WORKSPACE_ENABLED ? '3.5%' : 'From ₹0'}</span>
                <span className="stat-text">{MILAN_WORKSPACE_ENABLED ? 'Success Fee' : 'Free tier'}</span>
              </div>
            </div>
          </div>
          <div className="overview-visual">
            <div className="visual-grid">
              {services.slice(0, 4).map(s => (
                <div key={s.id} className="visual-card" style={{ borderColor: s.color }}>
                  <span>{s.icon}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="services-list">
        {services.map((service, idx) => (
          <div 
            key={service.id} 
            className={`service-block ${idx % 2 === 1 ? 'reverse' : ''}`}
          >
            <div className="service-content">
              <div className="service-icon" style={{ background: service.color }}>
                {service.icon}
              </div>
              <h3>
                {service.title}
                {service.isLive && (
                  <span className="service-live-pill" aria-label="Live feature">LIVE</span>
                )}
              </h3>
              <p>{service.description}</p>
              <ul className="service-features">
                {service.features.map((feature, i) => (
                  <li key={i}>
                    <span className="check" style={{ color: service.color }}>✓</span>
                    {feature}
                  </li>
                ))}
              </ul>
              {service.ctaLink && (
                <Link
                  to={service.ctaLink}
                  className="btn-primary"
                  style={{
                    display: 'inline-block',
                    marginTop: '1rem',
                    background: service.color,
                    textDecoration: 'none',
                  }}
                >
                  {service.ctaLabel || 'Open'}
                </Link>
              )}
            </div>
            <div className="service-visual" style={{ background: `${service.color}15` }}>
              <div className="visual-mockup">
                <div className="mockup-header">
                  <span></span><span></span><span></span>
                </div>
                <div className="mockup-content">
                  <span className="mockup-icon" style={{ background: service.color }}>{service.icon}</span>
                  <div className="mockup-lines">
                    <div style={{ background: service.color, opacity: 0.6 }}></div>
                    <div style={{ background: service.color, opacity: 0.4 }}></div>
                    <div style={{ background: service.color, opacity: 0.3 }}></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </section>

      <section className="services-cta">
        <h2>Ready to Get Started?</h2>
        <p>
          {MILAN_WORKSPACE_ENABLED
            ? 'Create your account and launch your first campaign'
            : 'Create a brand account and open Performance Hub'}
        </p>
        <div className="cta-buttons">
          <Link to="/signup" className="btn-primary">Get Started Free</Link>
          <Link to="/contact" className="btn-secondary">Contact Us</Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default Services;
